import React, { FC, useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import {
  Box,
  Card,
  Switch,
} from "@material-ui/core";
import { Bonus } from "../../types/bonus";
import { FilterDate } from "../../types/commonForm";
import { AppButton } from "../common/ctaButton/button";
import { BonusesTable } from "./shared/BonusesTable";
import { BonusViewModal } from "./shared/BonusViewModal";
import { Raffle } from "../../types/raffle";
import { BonusCreateEditModal } from "./shared/BonusCreateEditModal";

import "./index.css";

interface State {
  bonuses: Array<Bonus>;
  total: number;
  page: number;
  perPage: number;
  field: string;
  order: string;
  searchField: string;
  searchValue: string;
  statusFilter: Array<string>;
  startDate: FilterDate;
  endDate: FilterDate;
  onlyActive: boolean;
}

const EarlyBirdList: FC = () => {
  const [state, setState] = useState<State>({
    bonuses: Array<Bonus>(),
    total: 0,
    page: 0,
    perPage: 10,
    field: "_id",
    order: "desc",
    searchField: "pageTitle",
    searchValue: "",
    statusFilter: Array<string>(),
    startDate: {
      startDate: undefined,
      endDate: undefined,
    },
    endDate: {
      startDate: undefined,
      endDate: undefined,
    },
    onlyActive: false,
  });

  const dataProvider = useDataProvider();

  const [raffles, setRaffles] = useState<Array<Raffle>>([]);
  const [currentBonus, setCurrentBonus] = useState<Bonus>();
  const [modalsState, setModals] = useState({
    viewBonus: false,
    createEditBonus: false,
  });

  useEffect(() => {
    getBonuses(
      1,
      10,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      Array<string>(),
      state.startDate,
      state.endDate,
      state.onlyActive
    );
  }, [state.onlyActive]);

  const getBonuses = async (
    page: number,
    perPage: number,
    field: string,
    order: string,
    searchField: string,
    searchValue: string,
    statusFilter: Array<string>,
    startDate: FilterDate,
    endDate: FilterDate,
    onlyActive: boolean
  ): Promise<void> => {
    const resource = "/bonusDraw/get-all";
    const result = (await dataProvider.getCharities(resource, {
      pagination: { page, perPage },
      sort: { field, order },
      search: { field: searchField, value: searchValue },
      filter: {
        statusFilter: statusFilter,
        startDate: startDate,
        endDate: endDate,
      },
      onlyActive: onlyActive,
    })) as any;
    setState({
      ...state,
      bonuses: result.data.bonuses,
      total: result.data.allCount,
      page,
      perPage,
      field,
      order,
      searchField,
      searchValue,
      statusFilter,
    });
  };

  useEffect(() => {
    getRaffle( 1, 1000);
  },[])

  const getRaffle = async ( page, perPage) => {
    let allRaffles: Array<Raffle> = [];
    const inactiveRaffles = await dataProvider.getDreamhomeList("raffles", {
        filter: { isClosed: true },
        pagination: { page: page, perPage: perPage },
        sort: { field: "id", order: "ASC" },
    }) as any;
    const activeRaffles = await dataProvider.getDreamhomeList("raffles/active/cms",{
      filter: { },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "id", order: "ASC" },
    }) as any;
    
    allRaffles = (activeRaffles.data);
    allRaffles.push(...inactiveRaffles.data);
    allRaffles.sort((a, b) => +new Date(b.startAt) - +new Date(a.startAt));
    
    setRaffles(allRaffles);
}

  const getSortField = (field: string): string => {
    switch (field) {
      case "Name":
        return "pageTitle";
      case "Start Date":
        return "startAt";
      case "Finish Date":
        return "endAt";
      default:
        return field;
    }
  };

  const handleSort = (direction: string, field: string) => {
    const fieldName = getSortField(field);
    getBonuses(
      state.page,
      state.perPage,
      fieldName ? fieldName : field,
      direction,
      state.searchField,
      state.searchValue,
      state.statusFilter,
      state.startDate,
      state.endDate,
      state.onlyActive
    );
  };

  const search = (value: string, field: string) => {
    setState({ ...state, searchField: field, searchValue: value });
  };

  const clearSearch = () => {
    setState({ ...state, searchValue: "" });
  };

  const applySearch = (field: string) => {
    getBonuses(
      1,
      state.perPage,
      state.field,
      state.order,
      field,
      state.searchValue,
      state.statusFilter,
      state.startDate,
      state.endDate,
      state.onlyActive
    );
  };

  const handleCloseModals =
    (
      modalName:
        | 'viewBonus'
        | 'createEditBonus'
    ) =>
    () => {
      setModals((state) => ({
        ...state,
        [modalName]: false,
      }));
      setCurrentBonus(undefined);
    };

  const handleShowModals = (
    modalName: "viewBonus" | "createEditBonus"
  ) => {
    if (
      modalName === "viewBonus" ||
      modalName === "createEditBonus"
    ) {
      return (bonusId: string | undefined) => {
        const bonus = state.bonuses.find((i) => i._id === bonusId);
        setCurrentBonus(() => bonus);
        setModals((state) => ({
          ...state,
          [modalName]: true,
        }));
      };
    }
    return () => {
      setModals((state) => ({
        ...state,
        [modalName]: true,
      }));
    };
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    getBonuses(
      newPage + 1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
      state.startDate,
      state.endDate,
      state.onlyActive
    );
  };

  const handleLastPage = () => {
    const page = Math.ceil(state.total / state.perPage);
    getBonuses(
      page,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
      state.startDate,
      state.endDate,
      state.onlyActive
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const perPage = parseInt(event.target.value, 10);
    getBonuses(
      1,
      perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
      state.startDate,
      state.endDate,
      state.onlyActive
    );
  };

  const handleFirstPage = () => {
    getBonuses(
      1,
      state.perPage,
      state.field,
      state.order,
      state.searchField,
      state.searchValue,
      state.statusFilter,
      state.startDate,
      state.endDate,
      state.onlyActive
    );
  };

  return (
    <div className="bonus-draw">
      <div className="bonus-draw__top">
        <div className="bonus-draw__toggle">
          <Switch
            checked={state.onlyActive}
            onChange={() =>
              setState({ ...state, onlyActive: !state.onlyActive })
            }
            color="primary"
            name="checkedB"
            inputProps={{ "aria-label": "primary checkbox" }}
          />
          <p>only Active Early Bird Draws</p>
        </div>
        <AppButton onClick={handleShowModals("createEditBonus")} text="Add Bonus" />
      </div>
      <Box marginTop="0.5em">
        <Card>
          <BonusesTable
            bonuses={state.bonuses}
            handleSort={handleSort}
            search={search}
            applySearch={applySearch}
            searchValue={state.searchValue}
            clearSearch={clearSearch}
            handleShowModals={handleShowModals}
            page={state.page}
            perPage={state.perPage}
            collectionLength={state.bonuses.length}
            total={state.total}
            handleChangePage={handleChangePage}
            handleLastPage={handleLastPage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleFirstPage={handleFirstPage}
          />
        </Card>
        {modalsState.viewBonus && currentBonus && (
          <BonusViewModal
            bonus={currentBonus}
            raffles={raffles}
            closeModal={handleCloseModals("viewBonus")}
            showModal={modalsState.viewBonus}
          />
        )}
        {modalsState.createEditBonus && (
          <BonusCreateEditModal
            bonus={currentBonus}
            raffles={raffles}
            closeModal={handleCloseModals("createEditBonus")}
            showModal={modalsState.createEditBonus}
            refresh={() =>
              getBonuses(
                1,
                10,
                state.field,
                state.order,
                state.searchField,
                state.searchValue,
                Array<string>(),
                state.startDate,
                state.endDate,
                state.onlyActive
              )
            }
          />
        )}
      </Box>
    </div>
  );
};

export default EarlyBirdList;
